import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAccount } from "wagmi";

interface Bet {
  id: number;
  matchId: number;
  betType: "CORRECT_SCORE" | "REVERSE_PARLAY";
  predictedHome: number;
  predictedAway: number;
  amount: string;
  odds: string;
  potentialPayout: string;
  state: "PLACED" | "CANCELLED" | "SETTLED_WON" | "SETTLED_LOST" | "VOIDED";
  placedAt: string;
  payout: string | null;
  match?: {
    homeTeam: string;
    awayTeam: string;
    kickoffTime: string;
    homeScore: number | null;
    awayScore: number | null;
  };
}

const STATE_COLORS: Record<string, string> = {
  PLACED: "text-blue-400 bg-blue-900/30",
  CANCELLED: "text-gray-400 bg-gray-800",
  SETTLED_WON: "text-green-400 bg-green-900/30",
  SETTLED_LOST: "text-red-400 bg-red-900/30",
  VOIDED: "text-yellow-400 bg-yellow-900/30",
};

export function MyBetsPage() {
  const { address, isConnected } = useAccount();
  const [bets, setBets] = useState<Bet[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<string>("ALL");

  useEffect(() => {
    if (isConnected && address) {
      loadBets();
    }
  }, [isConnected, address]);

  async function loadBets() {
    // TODO: Read user bets from contract / Subgraph / backend API
    setLoading(true);
    try {
      setBets([]);
    } finally {
      setLoading(false);
    }
  }

  const filteredBets =
    filter === "ALL" ? bets : bets.filter((b) => b.state === filter);

  if (!isConnected) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold mb-6">My Bets</h2>
        <div className="text-center py-12 bg-gray-900 rounded-lg">
          <p className="text-gray-400 text-lg">Connect your wallet to view bets</p>
          <Link to="/" className="text-blue-400 hover:text-blue-300 mt-2 inline-block">
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">My Bets</h2>
        <Link to="/matches" className="text-blue-400 hover:text-blue-300 text-sm">
          Browse Matches
        </Link>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {["ALL", "PLACED", "SETTLED_WON", "SETTLED_LOST", "CANCELLED", "VOIDED"].map(
          (f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition ${
                filter === f
                  ? "bg-blue-600 text-white"
                  : "bg-gray-800 text-gray-400 hover:bg-gray-700"
              }`}
            >
              {f === "ALL" ? "All" : f.replace("_", " ")}
            </button>
          ),
        )}
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-800 rounded-lg" />
          ))}
        </div>
      ) : filteredBets.length === 0 ? (
        <div className="text-center py-12 bg-gray-900 rounded-lg">
          <p className="text-gray-400">
            {bets.length === 0
              ? "You haven't placed any bets yet"
              : "No bets match the selected filter"}
          </p>
          <Link
            to="/matches"
            className="inline-block mt-4 px-6 py-3 bg-blue-600 rounded-lg hover:bg-blue-700 transition"
          >
            Place a Bet
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBets.map((bet) => (
            <div
              key={bet.id}
              className="bg-gray-900 rounded-lg border border-gray-800 p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs px-2 py-1 rounded ${STATE_COLORS[bet.state] || ""}`}
                  >
                    {bet.state.replace("_", " ")}
                  </span>
                  <span className="text-xs text-gray-500">
                    {bet.betType === "REVERSE_PARLAY"
                      ? "Reverse Parlay"
                      : "Correct Score"}
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(bet.placedAt).toLocaleDateString()}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm">
                    {bet.match
                      ? `${bet.match.homeTeam} vs ${bet.match.awayTeam}`
                      : `Match #${bet.matchId}`}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {bet.betType === "REVERSE_PARLAY" ? "NOT " : ""}
                    {bet.predictedHome}-{bet.predictedAway} @ {parseFloat(bet.odds).toFixed(4)}x
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-mono">
                    {parseFloat(bet.amount).toFixed(2)} USDT
                  </p>
                  {bet.state === "SETTLED_WON" && bet.payout ? (
                    <p className="text-xs text-green-400 font-mono">
                      +{parseFloat(bet.payout).toFixed(2)} USDT
                    </p>
                  ) : bet.state === "PLACED" ? (
                    <p className="text-xs text-gray-500 font-mono">
                      Potential: {parseFloat(bet.potentialPayout).toFixed(2)}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
